import { Fragment, isValidElement, type ReactNode } from 'react';
import TruncatedText from '@/storybook/components/TruncatedText/TruncatedText';
import { highlightNodeText } from '@/storybook/components/TruncatedText/searchHighlightNodes';
import truncatedTextStyles from '@/storybook/components/TruncatedText/TruncatedText.module.css';
import { normalizeSearchTerms } from '@/storybook/utils/normalizeSearchTerms';
import type { CustomColumnDef } from '@/types/table';

type SearchAwareColumnDef<T> = Pick<CustomColumnDef<T>, 'getHighlightValue' | 'multiline'>;

type RenderSearchAwareContentOptions<T> = {
  columnDef: SearchAwareColumnDef<T>;
  cellValue: unknown;
  content: ReactNode;
  renderedValue: unknown;
  row: T;
  searchValue?: string;
  searchTerms?: string[];
};

const hasSearchMatch = (searchTerms: string[], value: string): boolean =>
  searchTerms.some(searchTerm => value.toLocaleLowerCase().includes(searchTerm));

const getPrimitiveValue = (value: unknown): string | null => {
  if (typeof value === 'string' || typeof value === 'number') {
    return String(value);
  }

  return null;
};

const hasOnlyChildrenProp = (props: { children?: ReactNode }): boolean =>
  Object.keys(props).every(propName => propName === 'children');

const getTextOnlyContentValue = (node: ReactNode): string | null => {
  if (typeof node === 'string' || typeof node === 'number') {
    return String(node);
  }

  if (Array.isArray(node)) {
    const textParts = node.map(getTextOnlyContentValue);

    return textParts.every((textPart): textPart is string => textPart !== null) ? textParts.join('') : null;
  }

  if (isValidElement<{ children?: ReactNode }>(node)) {
    const isSimpleElement =
      node.type === Fragment || (typeof node.type === 'string' && hasOnlyChildrenProp(node.props));

    if (!isSimpleElement) {
      return null;
    }

    if (node.props.children === undefined) {
      return '';
    }

    return getTextOnlyContentValue(node.props.children);
  }

  return null;
};

export const renderSearchAwareContent = <T,>({
  columnDef,
  cellValue,
  content,
  renderedValue,
  row,
  searchValue,
  searchTerms,
}: RenderSearchAwareContentOptions<T>): ReactNode => {
  const resolvedSearchTerms = searchTerms ?? normalizeSearchTerms(searchValue);

  if (!resolvedSearchTerms.length) {
    return content;
  }

  const isTopLevelTextWrapper =
    isValidElement<{ children?: ReactNode }>(content) &&
    (content.type === Fragment || typeof content.type === 'string') &&
    content.props.children !== undefined;
  const textOnlyContentValue = isTopLevelTextWrapper
    ? getTextOnlyContentValue(content.props.children)
    : getTextOnlyContentValue(content);
  const isChildlessElement = isValidElement<{ children?: ReactNode }>(content) && content.props.children === undefined;
  const primitiveRenderedValue = getPrimitiveValue(renderedValue);
  const highlightValue = columnDef.getHighlightValue?.(cellValue, row);
  const primitiveHighlightValue = getPrimitiveValue(highlightValue);

  if (
    !columnDef.multiline &&
    textOnlyContentValue !== null &&
    hasSearchMatch(resolvedSearchTerms, textOnlyContentValue)
  ) {
    return isTopLevelTextWrapper ? (
      <TruncatedText value={textOnlyContentValue} highlightValue={searchValue} highlightTerms={resolvedSearchTerms}>
        {content}
      </TruncatedText>
    ) : (
      <TruncatedText value={textOnlyContentValue} highlightValue={searchValue} highlightTerms={resolvedSearchTerms} />
    );
  }

  if (!columnDef.multiline && textOnlyContentValue === null && primitiveHighlightValue !== null) {
    if (hasSearchMatch(resolvedSearchTerms, primitiveHighlightValue)) {
      return (
        <TruncatedText
          value={primitiveHighlightValue}
          highlightValue={searchValue}
          highlightTerms={resolvedSearchTerms}
        />
      );
    }
  }

  if (!columnDef.multiline && isChildlessElement && primitiveHighlightValue !== null) {
    if (hasSearchMatch(resolvedSearchTerms, primitiveHighlightValue)) {
      return (
        <TruncatedText
          value={primitiveHighlightValue}
          highlightValue={searchValue}
          highlightTerms={resolvedSearchTerms}
        />
      );
    }
  }

  if (!columnDef.multiline && isChildlessElement && primitiveRenderedValue !== null) {
    if (hasSearchMatch(resolvedSearchTerms, primitiveRenderedValue)) {
      return (
        <TruncatedText
          value={primitiveRenderedValue}
          highlightValue={searchValue}
          highlightTerms={resolvedSearchTerms}
        />
      );
    }
  }

  return highlightNodeText(content, resolvedSearchTerms, truncatedTextStyles.highlight);
};
